import React, { useEffect, useState } from 'react';
import { apiErrorMessage } from '../api/abfApi';
import { Note, SmallButton } from '../onboarding/review/contractReviewStyles';
import { FieldLabel, Input } from '../onboarding/review/reviewStyles';
import { correctTapeRow, getTapeLoanRows, proposeRowExclusion, TapeLoanRow } from './periodApi';

interface Props {
  facilityId: number; tapeId: number; mayEdit: boolean; busy: boolean;
  onSave: (action: () => Promise<unknown>) => Promise<boolean>;
}
const TapeCorrectionPanel: React.FC<Props> = ({ facilityId, tapeId, mayEdit, busy, onSave }) => {
  const [rows, setRows] = useState<TapeLoanRow[]>([]);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<TapeLoanRow | null>(null);
  const [intent, setIntent] = useState<'correct' | 'exclude'>('correct');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let active = true;
    getTapeLoanRows(facilityId, tapeId, offset, query).then(result => { if (active) { setRows(result); setError(null); } })
      .catch(failure => { if (active) setError(apiErrorMessage(failure, 'Loan rows could not be loaded.')); });
    return () => { active = false; };
  }, [facilityId, tapeId, offset, refresh, query]);
  const open = (row: TapeLoanRow, what: 'correct' | 'exclude') => {
    setSelected(row); setIntent(what);
    setValues(Object.fromEntries(Object.entries(row.raw_payload || {}).map(([key, value]) => [key, String(value ?? '')])));
    setReason(''); setEvidence('');
  };
  return <div style={{ marginTop: 10 }}>
    {error && <Note role="alert">{error} <SmallButton onClick={() => setRefresh(n => n + 1)}>Retry</SmallButton></Note>}
    <form style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }} onSubmit={e => { e.preventDefault(); setOffset(0); setQuery(search.trim()); }}>
      <Input value={search} placeholder="Find a loan or obligor (e.g. RL-0043)" aria-label="Find a loan" onChange={e => setSearch(e.target.value)} style={{ flex: '1 1 260px' }} />
      <SmallButton type="submit" disabled={busy}>Find</SmallButton>
      {query && <SmallButton type="button" disabled={busy} onClick={() => { setSearch(''); setQuery(''); setOffset(0); }}>Show all</SmallButton>}
    </form>
    <table style={{ width: '100%' }}>
      <thead><tr><th>Source row</th><th>Asset</th><th>Balance</th><th>Action</th></tr></thead>
      <tbody>{rows.length === 0 ? <tr><td colSpan={4}>{query ? `No loan on this tape matches “${query}”.` : 'No loan rows.'}</td></tr>
        : rows.map(row => <tr key={row.id}><td>{row.raw_row_number}</td><td>{row.asset_identifier}</td><td>{Number(row.current_balance).toLocaleString()}</td>
        <td style={{ display: 'flex', gap: 6 }}>
          <SmallButton disabled={!mayEdit || busy} onClick={() => open(row, 'correct')}>Correct row</SmallButton>
          <SmallButton disabled={!mayEdit || busy} onClick={() => open(row, 'exclude')}>Propose exclusion</SmallButton>
        </td></tr>)}</tbody>
    </table>
    <div style={{ display: 'flex', gap: 8, margin: '10px 0' }}>
      <SmallButton disabled={offset === 0 || busy} onClick={() => setOffset(n => Math.max(0, n - 25))}>Previous</SmallButton>
      <span>Rows {rows.length ? offset + 1 : 0}–{offset + rows.length}</span>
      <SmallButton disabled={rows.length < 25 || busy} onClick={() => setOffset(n => n + 25)}>Next</SmallButton>
    </div>
    {selected && <fieldset style={{ border: '1px solid #34455c', borderRadius: 8 }}>
      <legend>{intent === 'exclude' ? 'Propose excluding' : 'Correct'} {selected.asset_identifier}</legend>
      {intent === 'exclude'
        ? <p>The loan stays on the tape until a checker other than you approves the exclusion. Its balance of {Number(selected.current_balance).toLocaleString()} is recorded as the excluded amount; the original row remains on record.</p>
        : <p>A released tape keeps its original snapshot. Saving creates a revision in this period for independent release; the approved base remains in force until its replacement is approved.</p>}
      {intent === 'correct' && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 8 }}>
        {Object.keys(values).map(key => <FieldLabel key={key}>{key.replace(/_/g, ' ')}<Input value={values[key]} onChange={e => setValues({ ...values, [key]: e.target.value })} /></FieldLabel>)}
      </div>}
      <FieldLabel>Reason<Input value={reason} placeholder={intent === 'exclude' ? 'Why this loan should not count as collateral' : ''} onChange={e => setReason(e.target.value)} /></FieldLabel>
      <FieldLabel>Supporting evidence reference<Input value={evidence} placeholder="e.g. servicer email 2026-11-03" onChange={e => setEvidence(e.target.value)} /></FieldLabel>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <SmallButton disabled={busy} onClick={() => setSelected(null)}>Cancel</SmallButton>
        {intent === 'exclude'
          ? <SmallButton disabled={!mayEdit || busy || !reason.trim() || !evidence.trim()} onClick={() => void onSave(
              () => proposeRowExclusion(facilityId, tapeId, selected.raw_row_number, reason, evidence)
            ).then(ok => { if (ok) { setSelected(null); setRefresh(n => n + 1); } })}>Propose exclusion for a checker</SmallButton>
          : <SmallButton disabled={!mayEdit || busy || !reason.trim() || !evidence.trim()} onClick={() => void onSave(
              () => correctTapeRow(facilityId, tapeId, selected.raw_row_number, values, reason, evidence)
            ).then(ok => { if (ok) { setSelected(null); setRefresh(n => n + 1); } })}>Save correction</SmallButton>}
      </div>
    </fieldset>}
  </div>;
};
export default TapeCorrectionPanel;
